import { useEffect, useMemo, useState } from 'react';
import { ProfileAvatar } from './ProfileAvatar';
import { loadReplayManifest, loadReplayVideo, requestReplayDownload, type ReplayManifest } from './lib/replay';
import { trackAnalytics } from './lib/analytics';
import { t } from './lib/i18n';

const resultLabel = (result: ReplayManifest['rounds'][number]['result']) => t(({
  same:'SAME', different:'DIFFERENT', correct:'READ CORRECTLY', surprise:'SURPRISE', survived:'SURVIVED', matched:'MATCHED'
} as const)[result]);

export function GameReplayActions({ sessionId, initData }: { sessionId:string; initData:string }) {
  const [manifest, setManifest] = useState<ReplayManifest | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadReplayManifest(sessionId, initData).then((value) => { if (!cancelled) setManifest(value); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [sessionId, initData]);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const highlight = useMemo(
    () => manifest?.rounds.find((round) => round.roundNumber === manifest.highlightRoundNumber) ?? null,
    [manifest]
  );

  const generate = async (): Promise<Blob | null> => {
    if (blob) return blob;
    if (!window.confirm(t('Your replay includes both players’ names, profile photos and game answers. Make it only if you’re both comfortable sharing that moment.'))) return null;
    setBusy(true);
    setError(null);
    try {
      const nextBlob = await loadReplayVideo(sessionId, initData);
      const nextUrl = URL.createObjectURL(nextBlob);
      if (url) URL.revokeObjectURL(url);
      setBlob(nextBlob);
      setUrl(nextUrl);
      trackAnalytics('replay_video_requested', { interactionType:manifest?.interactionType ?? 'same_brain' });
      return nextBlob;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('Could not make the replay yet.'));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const share = async (): Promise<void> => {
    const nextBlob = blob ?? await generate();
    if (!nextBlob) return;
    const file = new File([nextBlob], 'UTAGE-game-replay.mp4', { type:'video/mp4' });
    try {
      if (navigator.canShare?.({ files:[file] }) && navigator.share) {
        await navigator.share({ title:'UTAGE Game Replay', text:manifest?.shareCopy ?? 'Our UTAGE game replay', files:[file] });
        trackAnalytics('replay_video_shared', { interactionType:manifest?.interactionType ?? 'same_brain' });
        return;
      }
    } catch (caught) {
      if ((caught as DOMException)?.name === 'AbortError') return;
    }
    setError(t('Telegram could not open the share sheet on this device. You can still save the video below.'));
  };

  const browserDownload = (downloadUrl: string, fileName: string): void => {
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = fileName;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const save = async (): Promise<void> => {
    const nextBlob = blob ?? await generate();
    if (!nextBlob) return;
    setBusy(true);
    setError(null);
    try {
      const download = await requestReplayDownload(sessionId, initData);
      const webApp = window.Telegram?.WebApp;
      if (webApp?.downloadFile && (webApp.isVersionAtLeast?.('8.0') ?? true)) {
        webApp.downloadFile({ url:download.url, file_name:download.fileName }, (accepted) => {
          if (!accepted) setError(t('Download cancelled.'));
        });
        return;
      }
      browserDownload(download.url, download.fileName);
    } catch (caught) {
      // Last fallback for ordinary browsers: use the already-generated in-memory MP4.
      if (url) {
        browserDownload(url, 'UTAGE-game-replay.mp4');
        return;
      }
      setError(caught instanceof Error ? caught.message : t('Could not save the replay yet.'));
    } finally {
      setBusy(false);
    }
  };

  if (!manifest) return null;
  const first = manifest.players[0];
  const second = manifest.players[1];
  if (!first || !second) return null;

  return <section className="replay-card">
    <div className="replay-card-brand"><img src="/utage-symbol.png" alt=""/><span>{t('GAME REPLAY')}</span></div>
    <div className="replay-humans">
      <div><ProfileAvatar userId={first.userId} displayName={first.displayName} telegramPhotoUrl={first.telegramPhotoUrl} hasCustomPhoto={first.hasCustomPhoto} initData={initData}/><strong>{first.displayName}</strong></div>
      <span className="replay-link">×</span>
      <div><ProfileAvatar userId={second.userId} displayName={second.displayName} telegramPhotoUrl={second.telegramPhotoUrl} hasCustomPhoto={second.hasCustomPhoto} initData={initData}/><strong>{second.displayName}</strong></div>
    </div>
    <div className="replay-score"><small>{manifest.interactionLabel}</small><strong>{manifest.score.value} / {manifest.score.total}</strong><span>{manifest.score.label}</span></div>
    {highlight ? <div className="replay-highlight"><small>{t('THE MOMENT')}</small><strong>{highlight.question}</strong><span>{resultLabel(highlight.result)}</span></div> : null}
    {url ? <video className="replay-preview" src={url} controls playsInline preload="metadata"/> : null}
    <div className="replay-actions">
      {!blob
        ? <button className="primary-action" disabled={busy} onClick={() => void generate()}>{busy ? t('MAKING YOUR REPLAY…') : t('MAKE VIDEO REPLAY')}</button>
        : <>
          <button className="primary-action" disabled={busy} onClick={() => void share()}>{t('SHARE VIDEO')}</button>
          <button className="secondary-wide replay-download" disabled={busy} onClick={() => void save()}>{busy ? t('PREPARING…') : t('SAVE VIDEO')}</button>
        </>}
    </div>
    <p className="replay-privacy">{t('Includes both players’ profiles and answers. Nothing is posted automatically.')}</p>
    {error ? <p className="inline-error">{error}</p> : null}
  </section>;
}
